// Dark surfaces for the vendored Plotly charts.
//
// The chart classes under ../vendor are kept byte-for-byte, and they were
// written for a white page: Plotly's default text (#444), axis text from the
// `axisTextColor` option (black), light gridlines, `#555` titles. On a dark
// surface all of that disappears. Rather than fork every class, the ONE seam
// they all pass through — `Plotly.newPlot` / `Plotly.react` — is wrapped once
// (`installPlotlyTheme`, called by the loader), and a figure drawn on a dark
// background is re-coloured on the way in:
//
//  - text the background cannot carry (contrast under 3:1) turns light: the
//    layout font, axis tick / title text, legend, chart title, paper-anchored
//    annotations, colour bars, gauge numbers and axes, sankey labels, text a
//    trace draws OUTSIDE its marks;
//  - light gridlines / axis lines turn into faint light lines;
//  - a light plot fill turns transparent, so the card shows through.
//
// Text drawn INSIDE a bar, slice or tile, and annotations pinned to data
// cells (a heatmap's values), are left alone: they sit on their own colour and
// the chart already chose a contrasting text for it. A chart whose author gave
// it an opaque LIGHT background is left exactly as authored, as is every
// figure on a light surface — light mode cannot change through this file.

import { contrast, isDarkColor, luminance, parseRgba, type RGB } from './color'
import { surfaceBackground } from './surfaceTheme'
import { applyChartAdvFormat } from './chartAdvFormat'

const DARK = {
  text: '#e6e9ef',
  muted: '#b3bcc7',
  grid: 'rgba(255,255,255,0.10)',
  line: 'rgba(255,255,255,0.28)',
  gaugeBorder: '#39414f',
}

type Obj = Record<string, unknown>

function isObj(v: unknown): v is Obj {
  return !!v && typeof v === 'object' && !Array.isArray(v)
}

/** JSON-shaped deep copy (Plotly figures are plain data). */
function clone<T>(v: T): T {
  if (Array.isArray(v)) return v.map(clone) as unknown as T
  if (isObj(v)) {
    const out: Obj = {}
    for (const k of Object.keys(v)) out[k] = clone(v[k])
    return out as T
  }
  return v
}

/** Make `font.color` read on `bg`; `force` sets it when absent. */
function fixFont(font: unknown, bg: RGB, force = false): Obj | undefined {
  if (!isObj(font)) return force ? { color: DARK.text } : undefined
  const c = parseRgba(typeof font.color === 'string' ? font.color : null)
  if (!c) {
    if (font.color === undefined && force) font.color = DARK.text
    return font
  }
  if (c[3] > 0.2 && contrast([c[0], c[1], c[2]], bg) < 3) font.color = DARK.text
  return font
}

/** A light line colour (grid / axis) becomes a faint light one. */
function fixLine(o: Obj, key: string, to: string): void {
  const c = parseRgba(typeof o[key] === 'string' ? (o[key] as string) : null)
  if (c && c[3] > 0.2 && luminance([c[0], c[1], c[2]]) > 0.45) o[key] = to
}

/** A light opaque fill becomes transparent. */
function clearLightFill(o: Obj, key: string): void {
  const c = parseRgba(typeof o[key] === 'string' ? (o[key] as string) : null)
  if (c && c[3] > 0.5 && luminance([c[0], c[1], c[2]]) > 0.6) o[key] = 'rgba(0,0,0,0)'
}

function fixTitle(o: Obj, bg: RGB): void {
  if (isObj(o.title)) fixFont((o.title as Obj).font, bg)
  if (o.titlefont !== undefined) fixFont(o.titlefont, bg)
}

function fixAxis(ax: unknown, bg: RGB): void {
  if (!isObj(ax)) return
  if (ax.tickfont !== undefined) fixFont(ax.tickfont, bg)
  fixTitle(ax, bg)
  fixLine(ax, 'gridcolor', DARK.grid)
  fixLine(ax, 'zerolinecolor', DARK.line)
  fixLine(ax, 'linecolor', DARK.line)
  fixLine(ax, 'tickcolor', DARK.line)
  if (isObj(ax.rangeslider)) clearLightFill(ax.rangeslider as Obj, 'bgcolor')
}

function fixColorbar(cb: unknown, bg: RGB): void {
  if (!isObj(cb)) return
  if (cb.tickfont !== undefined) fixFont(cb.tickfont, bg)
  else cb.tickfont = { color: DARK.text }
  fixTitle(cb, bg)
  fixLine(cb, 'outlinecolor', DARK.line)
}

const AXIS_KEY = /^(x|y|z)axis\d*$/
const SUBPLOT_KEY = /^(polar|scene|ternary)\d*$/

function themeLayout(src: unknown, bg: RGB): Obj {
  const layout: Obj = isObj(src) ? clone(src) : {}
  layout.font = fixFont(isObj(layout.font) ? layout.font : {}, bg, true)
  clearLightFill(layout, 'plot_bgcolor')
  if (typeof layout.title === 'string') layout.title = { text: layout.title, font: { color: DARK.text } }
  else fixTitle(layout, bg)
  for (const k of Object.keys(layout)) {
    if (AXIS_KEY.test(k)) fixAxis(layout[k], bg)
    else if (SUBPLOT_KEY.test(k) && isObj(layout[k])) {
      const sp = layout[k] as Obj
      clearLightFill(sp, 'bgcolor')
      for (const ak of Object.keys(sp)) if (/axis$/.test(ak)) fixAxis(sp[ak], bg)
    }
  }
  if (isObj(layout.legend)) {
    const lg = layout.legend as Obj
    if (lg.font !== undefined) fixFont(lg.font, bg)
    fixTitle(lg, bg)
    clearLightFill(lg, 'bgcolor')
    fixLine(lg, 'bordercolor', DARK.line)
  }
  if (Array.isArray(layout.annotations)) {
    for (const a of layout.annotations) {
      if (!isObj(a)) continue
      // Pinned to the data (a heatmap cell's value) → it sits on that cell's
      // colour and keeps the colour the chart chose for it.
      const onData = (typeof a.xref === 'string' && a.xref !== 'paper' && !a.xref.endsWith(' domain'))
        || (typeof a.yref === 'string' && a.yref !== 'paper' && !a.yref.endsWith(' domain'))
      const boxed = parseRgba(typeof a.bgcolor === 'string' ? a.bgcolor : null)
      if (onData || (boxed && boxed[3] > 0.5)) continue
      fixFont(a.font ?? (a.font = {}), bg, true)
    }
  }
  if (isObj(layout.coloraxis)) fixColorbar((layout.coloraxis as Obj).colorbar, bg)
  if (!isObj(layout.modebar)) {
    layout.modebar = { bgcolor: 'rgba(0,0,0,0)', color: DARK.muted, activecolor: DARK.text }
  }
  return layout
}

const OUTSIDE_TEXT_TYPES = new Set(['scatter', 'scattergl', 'scatterpolar', 'scatterpolargl', 'scatterternary', 'sankey'])

function themeTrace(src: unknown, bg: RGB): unknown {
  if (!isObj(src)) return src
  const t = clone(src)
  const type = typeof t.type === 'string' ? t.type : 'scatter'
  // Text beside the marks (a line's point labels, sankey node names) sits on
  // the card; text inside a bar / slice / tile sits on its own colour.
  if (OUTSIDE_TEXT_TYPES.has(type)) {
    if (t.textfont !== undefined) fixFont(t.textfont, bg)
  } else if (t.textposition === 'outside') {
    if (t.textfont !== undefined) fixFont(t.textfont, bg)
  }
  if (t.outsidetextfont !== undefined) fixFont(t.outsidetextfont, bg)
  if (isObj(t.title)) fixFont((t.title as Obj).font, bg)
  if (isObj(t.colorbar)) fixColorbar(t.colorbar, bg)
  if (isObj(t.marker) && isObj((t.marker as Obj).colorbar)) fixColorbar((t.marker as Obj).colorbar, bg)
  if (type === 'indicator') {
    if (isObj(t.number)) fixFont((t.number as Obj).font, bg, true)
    else t.number = { font: { color: DARK.text } }
    if (isObj(t.gauge)) {
      const g = t.gauge as Obj
      clearLightFill(g, 'bgcolor')
      fixLine(g, 'bordercolor', DARK.gaugeBorder)
      if (isObj(g.axis)) {
        const ax = g.axis as Obj
        fixFont(ax.tickfont ?? (ax.tickfont = {}), bg, true)
        fixLine(ax, 'tickcolor', DARK.line)
      }
    }
  }
  if (type === 'sankey' && isObj(t.node)) fixLine((t.node as Obj).line as Obj ?? {}, 'color', DARK.gaugeBorder)
  return t
}

/**
 * The figure as drawn on the surface behind `gd`: unchanged on a light one,
 * re-coloured for a dark one. Never throws — a figure that cannot be read is
 * drawn as given.
 */
export function themeFigure(gd: unknown, data: unknown, layout: unknown): { data: unknown; layout: unknown } {
  try {
    const el = typeof gd === 'string' ? document.getElementById(gd) : (gd as Element | null)
    const paper = parseRgba(isObj(layout) && typeof layout.paper_bgcolor === 'string' ? layout.paper_bgcolor : null)
    const bg: RGB = paper && paper[3] > 0.5 ? [paper[0], paper[1], paper[2]] : surfaceBackground(el)
    if (!isDarkColor(bg)) return { data, layout }
    return {
      data: Array.isArray(data) ? data.map((t) => themeTrace(t, bg)) : data,
      layout: themeLayout(layout, bg),
    }
  } catch (err) {
    console.warn('[visual-engine] dark theme not applied to a chart:', err)
    return { data, layout }
  }
}

interface PlotlyLike {
  newPlot: (...args: unknown[]) => unknown
  react: (...args: unknown[]) => unknown
  __twastaTheme?: boolean
}

/** Wrap `newPlot` / `react` once so every chart passes through themeFigure —
 *  and, first, through the measure columns' ADV_FORMAT colours
 *  (chartAdvFormat.ts). Both also accept a figure object (`{data, layout,
 *  config}`) second. */
export function installPlotlyTheme(P: unknown): void {
  const plotly = P as PlotlyLike | null
  if (!plotly || plotly.__twastaTheme || typeof plotly.newPlot !== 'function') return
  const wrap = (orig: (...args: unknown[]) => unknown) => function (this: unknown, ...args: unknown[]) {
    const [gd, second, third, ...rest] = args
    if (isObj(second) && Array.isArray(second.data)) {
      const t = themeFigure(gd, applyChartAdvFormat(gd, second.data), second.layout)
      return orig.call(this, gd, { ...second, data: t.data, layout: t.layout }, third, ...rest)
    }
    const t = themeFigure(gd, applyChartAdvFormat(gd, second), third)
    return orig.call(this, gd, t.data, t.layout, ...rest)
  }
  plotly.newPlot = wrap(plotly.newPlot)
  if (typeof plotly.react === 'function') plotly.react = wrap(plotly.react)
  plotly.__twastaTheme = true
}

// The vendored classes' OWN text colour option. `axisTextColor` drives their
// axis text and — on the heatmap — whether its matrix view uses its dark
// palette. On a dark surface a black (or unset) value is the catalog default
// written for a white page, so it becomes white; a chart the author gave an
// opaque LIGHT background keeps everything as authored.
const AXIS_TEXT_NAMES = new Set(['axistextcolor', 'textcolor', 'axiscolor', 'labelcolor', 'axislabelcolor', 'ticklabelcolor'])
const CHART_BG_NAMES = new Set(['chartbackgroundcolor', 'backgroundcolor', 'bgcolor', 'chartbg', 'background', 'chartcolor'])

interface OptionLike { optionName?: string; defaultValue?: unknown }

export function themeVendorOptions<T extends OptionLike>(options: T[], dark: boolean): T[] {
  if (!dark) return options
  const lc = (v: unknown) => String(v ?? '').trim().toLowerCase()
  const bgOpt = options.find((o) => CHART_BG_NAMES.has(lc(o.optionName)))
  const bg = parseRgba(lc(bgOpt?.defaultValue))
  if (bg && bg[3] > 0.5 && !isDarkColor([bg[0], bg[1], bg[2]])) return options
  let found = false
  const out = options.map((o) => {
    if (!AXIS_TEXT_NAMES.has(lc(o.optionName))) return o
    found = true
    const c = parseRgba(lc(o.defaultValue))
    const unreadable = !lc(o.defaultValue) || (c !== null && isDarkColor([c[0], c[1], c[2]]))
    return unreadable ? { ...o, defaultValue: 'white' } : o
  })
  return found ? out : [...out, { optionName: 'axisTextColor', defaultValue: 'white' } as T]
}
